<template>
  <div v-if="subcategoryTabData && subcategoryTabData.length">
    <product-block v-for="(item, index) in subcategoryTabDataCollected" :key="index" :productInfo="item" />
  </div>
  <div v-else class="ta-c">No data found for subcategory tab</div>
</template>

<script setup lang="ts">
  import { useRoute } from 'vue-router';
  import { useMainStore } from '@/stores/main';
  import ProductBlock from '~/components/product/ProductBlock.vue';
  import InternalService from '~/services/internalService';

  definePageMeta({ middleware: ['check-channel-nodes', 'check-token'] });

  const route = useRoute();
  const store = useMainStore();

  const props = defineProps({
    subcategory: {
      type: Object,
      default: () => ({})
    }
  });

  // Reactive param for watching
  const currentSubcategoryTab = computed(() =>
    Array.isArray(route.params.subcategoryTab) ? route.params.subcategoryTab[0] : route.params.subcategoryTab
  );

  // Ensure data is loaded on SSR/CSR
  async function ensureChannelNodes() {
    if (store.channelNodes && store.channelNodes.length) return;
    const params = route.params;
    // 1) Try from provided subcategory prop (SSR-safe)
    let tab = null;
    const subFromProp = props.subcategory;
    if (subFromProp && Array.isArray(subFromProp.nodes)) {
      tab = subFromProp.nodes.find(
        (t) => String(t.nodeId) === String(params.subcategoryTab) || String(t.id) === String(params.subcategoryTab)
      );
    }
    // 2) Fallback to categories chain
    if (!tab) {
      const categories = Array.isArray(store.categories) ? store.categories : [];
      const cat = categories.find((c) => String(c.nodeId) === String(params.category) || String(c.id) === String(params.category));
      const sub =
        cat && Array.isArray(cat.nodes)
          ? cat.nodes.find((s) => String(s.nodeId) === String(params.subcategory) || String(s.id) === String(params.subcategory))
          : null;
      tab =
        sub && Array.isArray(sub.nodes)
          ? sub.nodes.find((t) => String(t.nodeId) === String(params.subcategoryTab) || String(t.id) === String(params.subcategoryTab))
          : null;
    }
    if (tab && Array.isArray(tab.nodes) && tab.nodes.length) {
      const ids = tab.nodes
        .map((n: any) => n.id ?? n.nodeId)
        .filter(Boolean)
        .join(',');
      store.channelNodes = ids;
    }
  }

  // Helper to set up channelNodes for the current subcategoryTab
  async function setupChannelNodes() {
    await ensureChannelNodes();
    const baseChannelNodes = store.channelNodes || '';
    if (baseChannelNodes.length > 0) {
      store.channelNodes = route.params.subcategoryTab + ',' + baseChannelNodes;
    } else {
      store.channelNodes = String(route.params.subcategoryTab);
    }
  }

  // Load initial data before useAsyncData (so immediate: false works)
  if (route.path && route.path.startsWith('/downloads')) {
    if (!Array.isArray(store.categories) || store.categories.length === 0) {
      await store.serverInit({ route, params: route.params });
    }
    await setupChannelNodes();
    await store.setSubcategoryTabData({ params: route.params, checkData: { check: false } });
  }

  // Auto-refetch when subcategoryTab param changes
  const { data: subcategoryTabResult } = await useAsyncData(
    `downloadsSubcategoryTabData-${currentSubcategoryTab.value}`,
    async () => {
      if (!Array.isArray(store.categories) || store.categories.length === 0) {
        await store.serverInit({ route, params: route.params });
      }
      // On client-side tab changes, recalculate channelNodes from the tab structure
      await setupChannelNodes();

      await store.setSubcategoryTabData({ params: route.params, checkData: { check: false } });
      return store.subcategoryTabData;
    },
    {
      // Watch this param and refetch when it changes
      watch: [currentSubcategoryTab],
      immediate: false // Prevents duplicate call on mount
    }
  );

  const subcategoryTabData = computed(() => store.subcategoryTabData);

  function findNodeIdIndexInArray(arr: any[], nodeId: any): number {
    if (!arr) return -1;
    return arr.findIndex((item: any) => item.nodeId === nodeId);
  }

  const subcategoryTabIndex = computed(() => findNodeIdIndexInArray(props.subcategory.nodes, route.params.subcategoryTab));

  const subcategoryTabDataCollected = computed(() => {
    let productsArr: any[] = [];
    let transformedArr: any[] = [];
    let productCard = {
      items: [] as any[],
      productBlockType: 'ProductCard'
    };
    let productOverview = {
      items: [] as any[],
      productBlockType: 'ProductOverview'
    };
    let productBlank = {
      external: { title: '' },
      items: [] as any[],
      id: null,
      productBlockType: ''
    };

    // if we have channelNodes we add them to our result
    let channelNodes = null;
    if (
      props.subcategory &&
      props.subcategory.nodes &&
      subcategoryTabIndex.value > -1 &&
      props.subcategory.nodes[subcategoryTabIndex.value]
    ) {
      channelNodes = props.subcategory.nodes[subcategoryTabIndex.value] ? props.subcategory.nodes[subcategoryTabIndex.value].nodes : null;
    }
    let isChannelNodesPresent = false;
    if (channelNodes) isChannelNodesPresent = channelNodes.length > 0;
    if (isChannelNodesPresent) {
      for (let channelNode of channelNodes) {
        let copy = Object.assign({}, productBlank);
        copy.id = channelNode.id;
        copy.external.title = channelNode.external.title;
        productsArr.push(copy);
      }
    }
    for (let productBlock of subcategoryTabData.value) {
      if (productBlock.productBlockType == 'ProductOverview') {
        if (isChannelNodesPresent) {
          let index = productsArr.findIndex((productOverview) => productOverview.id === productBlock.parentId);
          if (index == -1) {
            productOverview.items.push(productBlock);
          } else {
            productsArr[index].productBlockType = 'ProductOverview';
            productsArr[index].items.push(productBlock);
          }
        } else {
          productOverview.items.push(productBlock);
        }
      } else if (productBlock.productBlockType == 'ProductCard') {
        if (isChannelNodesPresent) {
          let index = productsArr.findIndex((productCard) => productCard.id === productBlock.parentId);
          if (index == -1) {
            productCard.items.push(productBlock);
          } else {
            productsArr[index].productBlockType = 'ProductCard';
            productsArr[index].items.push(productBlock);
          }
        } else {
          productCard.items.push(productBlock);
        }
      } else {
        transformedArr.push(productBlock);
      }
    }
    const productCardsArr = productsArr.filter((productBlock) => productBlock.productBlockType == 'ProductCard');
    const productOverviewsArr = productsArr.filter((productBlock) => productBlock.productBlockType == 'ProductOverview');
    if (productCardsArr.length > 0) transformedArr = [...transformedArr, ...productCardsArr];
    if (productCard.items.length > 0) transformedArr.push(productCard);
    if (productOverviewsArr.length > 0) transformedArr = [...transformedArr, ...productOverviewsArr];
    if (productOverview.items.length > 0) transformedArr.push(productOverview);
    return transformedArr;
  });

  onUpdated(() => {
    // Scroll logic for tab and sidebar navigation
    if (store.tabChangeState) {
      const header = document.getElementById('header');
      const banner = document.getElementById('banner');
      let currentPos = header ? header.getBoundingClientRect().height : 0;
      if (banner) currentPos += banner.getBoundingClientRect().height;
      if (window.scrollY >= currentPos) {
        const nav = document.getElementById('navigation');
        if (nav) {
          const navPos = window.scrollY + nav.getBoundingClientRect().top;
          window.scrollTo(0, navPos);
        }
      }
      setTimeout(() => {
        store.setTabChangeState(false);
      }, 0);
    }
    if (store.sidebarChangeState) {
      window.scrollTo(0, 0);
      store.setSidebarChangeState(false);
    }
  });
</script>
